import React, { useState, useEffect, useRef } from 'react';
import Imgix from 'react-imgix';
import LoadingSpinner from './LoadingSpinner';

interface ImgixMediaProps {
  src: string;
  width?: number;
  height?: number;
  alt?: string;
  htmlAttributes?: React.ImgHTMLAttributes<HTMLImageElement>;
  imgixParams?: { [key: string]: any };
  className?: string;
}

/**
 * ImgixMedia component that renders either an image or video based on the fm parameter
 * If imgixParams.fm === 'mp4', renders a video tag, otherwise renders the standard Imgix component
 * Handles HTTP 423 status for videos being processed with a spinner and polls every 20 seconds
 */
const ImgixMedia: React.FC<ImgixMediaProps> = ({
  src,
  width,
  height,
  alt,
  htmlAttributes,
  imgixParams = {},
  className,
}) => {
  // Check if the format is set to mp4
  const isVideo = imgixParams && imgixParams.fm === 'mp4';

  // State for video processing status
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Build the full URL with all parameters for the video or image
  const buildUrl = () => {
    const baseUrl = src;
    const params = new URLSearchParams();

    // Add all parameters except 'fm' which we handle specially for videos
    Object.entries(imgixParams).forEach(([key, value]) => {
      if (!isVideo || key !== 'fm') {
        params.append(key, String(value));
      }
    });

    // For videos, ensure 'fm' is set to 'mp4'
    if (isVideo) {
      params.append('fm', 'mp4');
    }

    // Combine the URL and parameters
    return `${baseUrl}?${params.toString()}`;
  };

  // Function to check if the video is ready
  const checkVideoStatus = async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });

      // If status is 200, video is ready
      if (response.status === 200) {
        setIsProcessing(false);

        // Clear the interval if it exists
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        return true;
      }
      // If status is 423, video is still processing
      else if (response.status === 423) {
        setIsProcessing(true);
        return false;
      }
      // For other statuses, consider as error and stop polling
      else {
        setIsProcessing(false);
        console.error(`Video check failed with status: ${response.status}`);

        // Clear the interval if it exists
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        return false;
      }
    } catch (error) {
      console.error('Error checking video status:', error);
      return false;
    }
  };

  // Start polling for video status whenever videoUrl changes
  useEffect(() => {
    if (isVideo) {
      const url = buildUrl();
      setVideoUrl(url);

      // Initial check
      checkVideoStatus(url);

      // Set up polling every 10 seconds
      pollingIntervalRef.current = setInterval(() => {
        checkVideoStatus(url);
      }, 10000);

      // Clean up on unmount or when URL changes
      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [isVideo, src, JSON.stringify(imgixParams)]);

  // For videos
  if (isVideo) {
    // If the video is processing, show a spinner
    if (isProcessing) {
      return (
        <div
          className={`relative bg-gray-800 ${className}`}
          style={{ width: width || '100%', height: height || '100%' }}
        >
          {/* For hero image (usually width=1920, height=600), position spinner in bottom third */}
          {/* For product images (width=400, height=400), ensure spinner is perfectly centered */}
          <div
            className={`absolute ${width === 1920 ? 'bottom-1/4 left-1/2 transform -translate-x-1/2' : 'inset-0 flex items-center justify-center'}`}
          >
            <LoadingSpinner
              width={width === 400 ? 100 : width}
              height={width === 400 ? 100 : height}
              className="flex-col"
            />
          </div>
        </div>
      );
    }

    // If the video is ready, show the video element
    return (
      <video
        src={videoUrl}
        width={width}
        height={height}
        controls
        autoPlay
        loop
        muted
        playsInline
        className={className}
        onError={(e) => {
          console.error('Video error:', e);
          // If there's an error loading the video, check if it's still processing
          checkVideoStatus(videoUrl);
        }}
      >
        Your browser does not support the video tag.
      </video>
    );
  }

  // Default to standard Imgix image rendering
  return (
    <Imgix
      src={src}
      width={width}
      height={height}
      alt={alt}
      htmlAttributes={htmlAttributes}
      imgixParams={imgixParams}
      className={className}
    />
  );
};

export default ImgixMedia;